// quick-chips-helper.ts — pure function port of monolith buildQuickChips
// (audit §9.3, static/index.html lines 3590-3711).
//
// Two-layer chip generation:
//   1. Result-aware: analyse provider/continent distribution in the cards
//      and suggest natural pivots (e.g. "AWS instead" when all cards are
//      GCP, "in EU" when all cards are US).
//   2. Param-based fallback: currency / price-unit / region toggles +
//      "cheaper" as a sort hint.
//
// Contract:
//   - `getChips(serverChips, results, params)` is the public entry point.
//     Prefers LLM-emitted chips when present (BFF feature flag LLM_CHIPS),
//     otherwise computes deterministic chips from results + params.
//   - Cap is 5 — chips should feel like a menu, not a wall.
//   - Pure function, no globals; all inputs are explicit.
//
// IMPORTANT: emit `prompt` (the refine text actually sent to BFF) and
// `label` (the visible button text) separately. The monolith's getChips
// takes server-side chips as `{label, prompt}` objects already; for
// fallback chips we generate both fields with sensible defaults.

import type { ResultCard, SearchParams, SuggestedChip } from '../types/search.js';

const MAX_CHIPS = 5;

/**
 * Public entry point. Returns up to 5 chips, prefering server-emitted
 * SuggestedChip[] when available and non-empty.
 */
export function getChips(
  serverChips: SuggestedChip[] | undefined | null,
  results: ResultCard[] | undefined | null,
  params: SearchParams | null | undefined,
): SuggestedChip[] {
  if (Array.isArray(serverChips) && serverChips.length > 0) {
    return serverChips.slice(0, MAX_CHIPS);
  }
  return buildQuickChips(results ?? [], params ?? null);
}

/**
 * Deterministic fallback chip builder. Result-aware (provider/continent
 * pivots) followed by param-based (currency, price-unit, region, cheaper).
 */
export function buildQuickChips(
  results: ResultCard[],
  params: SearchParams | null,
): SuggestedChip[] {
  const out: SuggestedChip[] = [];
  const seen = new Set<string>();

  function add(label: string, prompt: string): void {
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ label, prompt });
  }

  // --- 0. K8s deploy-mode pivots (highest priority for K8s results) ---
  // The mode (worker | asg | both) is the most consequential K8s refinement,
  // so offer it first. The prompts map to k8s_deploy_mode set-actions in the
  // refine grammar; switching INTO autoscaling carries a real 2–16 bracket so
  // the BFF doesn't inherit a frozen 1/1 from the prior worker turn.
  const k8sCard = results.find((c) => c.workloadType === 'kubernetes');
  if (k8sCard) {
    // Prompts lead with the ACTION GRAMMAR keywords ("add an autoscaling group",
    // "autoscaling group only", "add a static worker node", "drop autoscaling")
    // and frame the change as a mode SWITCH, not a field deletion — a weaker
    // refine model can otherwise read "no static worker" as `clear k8s_worker_count`
    // (a delete) instead of `set k8s_deploy_mode=asg` (the mode switch). Labels are
    // distinct across modes so the label-keyed dedup never collides.
    const mode = k8sCard.k8sMode || 'worker';
    if (mode === 'worker') {
      add('Add autoscaling', 'add an autoscaling group from 2 to 16 nodes');
      add('Autoscaling only', 'switch to an autoscaling group only, from 2 to 16 nodes');
    } else if (mode === 'asg') {
      add('Add a static node', 'add a static worker node alongside the autoscaling group');
      add('Single static node', 'switch to a single static worker node, drop autoscaling');
    } else if (mode === 'both') {
      add('Drop autoscaling', 'drop autoscaling, keep just the static worker node');
      add('Drop static node', 'switch to an autoscaling group only, drop the static worker node');
    }
  }

  // --- 1. Result-aware suggestions ---
  const hasResults = results.length > 0;
  const providerSet = new Set<string>();
  const continentSet = new Set<string>();
  if (hasResults) {
    for (const c of results) {
      if (c.provider) providerSet.add(c.provider);
      if (c.continent) continentSet.add(c.continent);
    }
  }
  const providers = Array.from(providerSet);
  const continents = Array.from(continentSet);

  // Provider pivot: when ALL results land on one provider, suggest 1-2
  // swaps to other top publics. When multiple providers are present,
  // suggest narrowing to each (max 2). Don't exclude when user has an
  // explicit providers filter — chip click is opt-in, suggesting
  // alternatives is the whole point.
  if (providers.length === 1) {
    const current = providers[0]!.toLowerCase();
    const alts = ['AWS', 'GCP', 'Azure'].filter((p) => p.toLowerCase() !== current);
    for (const alt of alts.slice(0, 2)) {
      add(`${alt} instead`, `show on ${alt} instead`);
    }
  } else if (providers.length > 1) {
    for (const prov of providers.slice(0, 2)) {
      add(`only ${prov}`, `only ${prov}`);
    }
  }

  // Continent pivot: when all cards on one continent and user didn't
  // explicitly pin a region, suggest the obvious alternative.
  if (continents.length === 1 && (!params || !params.region)) {
    switch (continents[0]) {
      case 'Europe':
        add('in US', 'show in US regions');
        break;
      case 'North America':
      case 'Asia':
        add('in EU', 'show in Europe');
        break;
    }
  }

  // --- 2. Param-based generic chips (fallback / always) ---

  // Provider narrowing — only when result-aware didn't cover it.
  if (out.length === 0 || providers.length === 0) {
    if (params && params.providers && params.providers.length > 1) {
      for (const prov of params.providers) {
        add(`only ${prov}`, `only ${prov}`);
      }
    } else if (!params || !params.providers || params.providers.length === 0) {
      if (!hasResults) {
        // No results AND no params → static defaults so the user can still refine.
        add('only AWS', 'only AWS');
        add('only GCP', 'only GCP');
      }
    }
  }

  // Currency toggle.
  if (params) {
    if ((params.currency || 'EUR') === 'EUR') {
      add('in USD', 'in USD');
    } else {
      add('in EUR', 'in EUR');
    }
  } else if (out.length < 4) {
    add('in USD', 'in USD');
  }

  // Price unit toggle.
  if (params) {
    if ((params.price_unit || 'hour') === 'hour') {
      add('per month', 'per month');
    } else {
      add('per hour', 'per hour');
    }
  }

  // Region — only if no result-aware continent chip was added.
  if (continents.length !== 1) {
    if (!params || !params.region) {
      add('in Frankfurt', 'in Frankfurt');
    } else if (params.region !== 'eu' && params.region !== 'europe') {
      add('in Europe', 'in Europe');
    }
  }

  // Always: cheaper as universal sort intent.
  add('cheaper', 'cheaper');

  return out.slice(0, MAX_CHIPS);
}
